import classNames from 'classnames/bind';
import styles from './Roofs.module.scss';
import TableClassicReport from '../base/TableClassicReport';
import { useEffect, useState } from 'react';
import { IPaginationReport, IRoofs } from '~/utils/interface/report';
import reportApi from '~/apis/report';
import { ColumnType } from '~/utils/types/common';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

const STATUS_ROOFS = [
  {
    value: "LEAD",
    label: "Lead",
  },
  {
    value: "APPOINTMENT_SCHEDULED",
    label: "Appointment Scheduled",
  },
  {
    value: "FILLING_CLAIM",
    label: "Filling Claim",
  },
  {
    value: "WAITING_ON_SCOPE",
    label: "Waiting on Scope",
  },
  {
    value: "SUPPLEMENTING",
    label: "Supplementing",
  },
  {
    value: "ESTIMATING",
    label: "Estimating",
  },
  {
    value: "ESTIMATE_SENT",
    label: "Estimate Sent",
  },
  {
    value: "COMMERCIAL",
    label: "Commercial",
  },
  {
    value: "HOLD_SIGNING",
    label: "Hold Signing",
  },
  {
    value: "SIGNED_CONTRACT",
    label: "Signed Contract",
  },
  {
    value: "PRODUCTION_REVIEW",
    label: "Production Review",
  },
  {
    value: "LOST",
    label: "Lost",
  },
  {
    value: "MATERIAL_ORDERED",
    label: "Material Ordered",
  },
  {
    value: "SECONDARY_TRADES",
    label: "Secondary Trades",
  },
  {
    value: "JOB_SCHEDULED",
    label: "Job Scheduled",
  },
  {
    value: "JOB_IN_PROGRESS",
    label: "Job In Progress",
  },
  {
    value: "FINAL_WALKTHROUGH",
    label: "Final Walkthrough",
  },
  {
    value: "JOB_COMPLETED",
    label: "Job Completed",
  },
  {
    value: "INVOICED_INSURANCE",
    label: "Invoiced Insurance",
  },
  {
    value: "INVOICED_COMMERCIAL",
    label: "Invoiced Commercial",
  },
  {
    value: "PAID_N_CLOSED",
    label: "Paid & Closed",
  },
  {
    value: "PAID_N_HAPPY",
    label: "Paid & Happy",
  },
  {
    value: "PAID_N_LEFT_REVIEW",
    label: "Paid & Left Review",
  },
  {
    value: "DELINQUENT_DO_NOT_ADD_JOBS",
    label: "Delinquent (Do Not Add Jobs)",
  },
];
const Roofs = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IRoofs[]>([]);
  const [pagination, setPagination] = useState<IPaginationReport>({
    totalItems: 0,
    totalPage: 0,
    page: 1,
    limit: 10,
    skip: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await reportApi.getRoofs({ page: pagination.page });
        setData(response.data?.data?.responses ?? []);
        setPagination(response.data?.data?.pagination ?? {
          totalItems: 0,
          totalPage: 0,
          page: 1,
          limit: 10,
          skip: 0,
        });
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch roofs', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [pagination.page]);

  const columns: Array<ColumnType> = [
    {
      id: "trade",
      labelHeader: "Trade 1",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.trade || "-"}</p>
        </div>
      ),
    },
    {
      id: "addressLine",
      labelHeader: "Address Line 1",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.addressLine || "-"}</p>
        </div>
      ),
    },
    {
      id: "zip",
      labelHeader: "Zip",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.zip || "-"}</p>
        </div>
      ),
    },
    {
      id: "shingleManufacturer",
      labelHeader: "Shingle Manufacturer",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.shingleManufacturer || "-"}</p>
        </div>
      ),
    },
    {
      id: "shingleColor",
      labelHeader: "Shingle Color",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.shingleColor || "-"}</p>
        </div>
      ),
    },
    {
      id: "saleRep",
      labelHeader: "Sale Rep",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.saleRep.name || "-"}</p>
        </div>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{STATUS_ROOFS.find(item => item.value === roofs.status)?.label || "-"}</p>
        </div>
      ),
    },
    {
      id: "dateStatusChanges",
      labelHeader: "Date Status Changes",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{dayjs(roofs.dateStatusChanges).format("MM/DD/YYYY hh:mm A") || "-"}</p>
        </div>
      ),
    },
    {
      id: "type",
      labelHeader: "Type",
      renderCell: (roofs: IRoofs) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{roofs.recordType || "-"}</p>
        </div>
      ),
    },
  ];

  return (
    <TableClassicReport
      title="Roofs"
      columns={columns}
      data={data}
      page={pagination.page}
      totalPages={pagination.totalPage}
      totalItems={pagination.totalItems}
      itemsPerPage={pagination.limit}
      onPageChange={(page) => {
        setPagination({ ...pagination, page });
      }}
      isLoading={loading}
    />
  );
};

export default Roofs;
